import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { Pill } from "./Ui";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { useAuth, hasPermission } from "../context/AuthContext";
import AdminEntityActions from "./AdminEntityActions";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  LogOut,
  Paperclip,
  ShieldAlert,
  XCircle,
} from "lucide-react";

import { roleLabel } from "../lib/roleMeta";
import { formatIndiaDateTime } from "../lib/indiaTime";
import GatePassPhotosPanel from "./GatePassPhotos";

function formatAt(iso) {
  return formatIndiaDateTime(iso) || iso || "—";
}

function EventIcon({ kind }) {
  if (kind === "gate_check_out") return <LogOut className="h-4 w-4 text-red-600" />;
  if (kind === "exit_approval") return <ShieldAlert className="h-4 w-4 text-amber-600" />;
  if (kind === "gate_check_in") return <CheckCircle2 className="h-4 w-4 text-primary" />;
  if (kind === "gate_photo") return <Paperclip className="h-4 w-4 text-primary" />;
  return <Clock className="h-4 w-4 text-muted-foreground" />;
}

async function readSupportFiles(fileList) {
  const files = Array.from(fileList || []);
  const docs = [];
  for (const f of files) {
    if (f.size > 512000) {
      toast.error(`Skipped ${f.name} — max 500 KB per file`);
      continue;
    }
    const data_url = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });
    docs.push({ name: f.name, data_url });
  }
  return docs;
}

export default function GatePassDetailDialog({ passId, open, onOpenChange, onUpdated, onDeleted }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [supportFiles, setSupportFiles] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");

  const load = () => {
    if (!passId) return;
    setLoading(true);
    api
      .get(`/gate-passes/${passId}`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && passId) {
      setApprovalNotes("");
      setSupportFiles(null);
      setReviewNotes("");
      load();
    } else if (!open) {
      setDetail(null);
    }
  }, [open, passId]);

  const gp = detail?.gate_pass;
  const canApprove = hasPermission(user, "gate.exit.approve");
  const canRequest = hasPermission(user, "gate.exit.request");
  const canForce = hasPermission(user, "gate.exit.force");
  const canUploadPhotos =
    hasPermission(user, "gate.pass.create") || hasPermission(user, "gate.pass.exit");

  const checkOut = async (force = false) => {
    try {
      setBusy(true);
      const { data } = await api.post(`/gate-passes/${passId}/exit`, force ? { force: true } : {});
      const cleared = (data?.cancelled_outward_dispatch_ids || []).length;
      if (cleared) {
        toast.success(
          force
            ? "Force empty exit recorded — truck removed from outward queues"
            : "Checked out empty — truck removed from outward queues",
        );
      } else {
        toast.success(force ? "Force check-out recorded" : "Checked out");
      }
      onUpdated?.();
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitApprovalRequest = async () => {
    const notes = approvalNotes.trim();
    if (!notes) {
      toast.error("Notes are required for manager approval");
      return;
    }
    try {
      setBusy(true);
      const support_docs = supportFiles?.length ? await readSupportFiles(supportFiles) : [];
      await api.post(`/gate-passes/${passId}/exit-request`, {
        reason_notes: notes,
        support_docs,
      });
      toast.success("Sent to manager for approval");
      setApprovalNotes("");
      setSupportFiles(null);
      onUpdated?.();
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const reviewRequest = async (decision) => {
    const rid = detail?.exit_request?.id;
    if (!rid) return;
    const notes = reviewNotes.trim();
    if (decision === "REJECT" && !notes) {
      toast.error("Review notes are required when rejecting");
      return;
    }
    try {
      setBusy(true);
      await api.post(`/gate-passes/${passId}/exit-request/${rid}/review`, {
        decision,
        review_notes: notes,
      });
      toast.success(decision === "APPROVE" ? "Exit approved" : "Exit request rejected");
      setReviewNotes("");
      onUpdated?.();
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gate-pass-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-lg">{gp?.code || "Gate pass"}</DialogTitle>
          <DialogDescription>
            {loading
              ? "Loading pass timeline…"
              : `${(gp?.purpose || "").replace(/_/g, " ")} · ${gp?.vehicle_number || "—"} · ${roleLabel(user?.role)}`}
          </DialogDescription>
        </DialogHeader>

        {loading && !detail && (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
        )}

        {detail && gp && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Info label="Status">
                <Pill tone={gp.status === "IN" ? "sage" : "default"}>{gp.status}</Pill>
              </Info>
              <Info label="Driver / visitor">{gp.driver_name}</Info>
              <Info label="Phone">{gp.driver_phone || "—"}</Info>
              <Info label="Party">{gp.party_name || "—"}</Info>
              <Info label="Check in">{formatAt(gp.created_at)}</Info>
              <Info label="Check out">{formatAt(gp.out_at)}</Info>
              {gp.notes ? (
                <div className="col-span-2 sm:col-span-3">
                  <Info label="Notes">{gp.notes}</Info>
                </div>
              ) : null}
            </div>

            <GatePassPhotosPanel
              gatePass={gp}
              canUpload={canUploadPhotos}
              onUpdated={() => {
                load();
                onUpdated?.();
              }}
            />

            {detail.check_out_blockers?.length > 0 && gp.status === "IN" && !detail.can_check_out && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="gate-checkout-blockers">
                <div className="flex items-center gap-2 font-medium text-amber-900 mb-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {detail.outward_post_load_blocked
                    ? "Finish dispatch before exit"
                    : detail.outward_empty_exit_allowed
                      ? "Empty exit needs approval"
                      : "Cannot check out yet"}
                </div>
                <ul className="list-disc pl-5 space-y-1 text-amber-800 text-xs">
                  {detail.check_out_blockers.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
                {detail.outward_post_load_blocked ? (
                  <p className="text-xs text-amber-900 mt-2">
                    Loading is already done. Complete gross weigh and dispatch accounts — empty exit is not allowed after loading.
                  </p>
                ) : detail.outward_empty_exit_allowed ? (
                  <p className="text-xs text-amber-900 mt-2">
                    Truck has not finished loading. Request manager approval to send it out empty — it will be removed from Sales Link / Tare / Loading queues.
                  </p>
                ) : null}
              </div>
            )}

            {detail.has_approved_override && gp.status === "IN" && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  {detail.outward_empty_exit_allowed
                    ? "Manager approved empty exit. Check-out will clear this truck from the outward phase queues."
                    : "Manager approved check-out despite pending mill steps. Guard may proceed."}
                </span>
              </div>
            )}

            {detail.exit_request?.status === "PENDING" && canApprove && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
                <div className="font-medium text-blue-900 mb-1">Pending your approval</div>
                <p className="text-blue-800 text-xs whitespace-pre-wrap">{detail.exit_request.reason_notes}</p>
                {detail.exit_request.support_docs?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {detail.exit_request.support_docs.map((doc, i) => (
                      <a
                        key={i}
                        href={doc.data_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs inline-flex items-center gap-1 text-blue-700 underline"
                      >
                        <Paperclip className="h-3 w-3" />
                        {doc.name || `Document ${i + 1}`}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {detail.purchases?.length > 0 && (
              <div>
                <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Linked purchases</h4>
                <div className="space-y-2">
                  {detail.purchases.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border rounded-md px-3 py-2">
                      <span className="font-mono text-xs">{p.code}</span>
                      <span className="text-muted-foreground text-xs">{p.state?.replace(/_/g, " ")}</span>
                      <Link to={`/purchases/${p.id}`} className="text-xs link-brand">
                        Open purchase →
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Event timeline</h4>
              <div className="space-y-3 max-h-56 overflow-y-auto pr-1" data-testid="gate-pass-events">
                {detail.events?.length === 0 && (
                  <p className="text-xs text-muted-foreground">No events recorded.</p>
                )}
                {detail.events?.map((ev, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="mt-0.5 shrink-0">
                      <EventIcon kind={ev.kind} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-foreground">{ev.title}</div>
                      <div className="text-xs text-muted-foreground">{formatAt(ev.at)}{ev.by ? ` · ${ev.by}` : ""}</div>
                      {ev.detail && <div className="text-xs mt-0.5 text-muted-foreground">{ev.detail}</div>}
                      {ev.purchase_code && (
                        <div className="text-xs mt-0.5">
                          <Link to={`/purchases/${ev.purchase_id}`} className="link-brand">
                            {ev.purchase_code}
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {gp.status === "IN" && detail.can_request_exit_approval && canRequest && (
              <div className="border rounded-md p-4 space-y-3 bg-muted/50/80" data-testid="gate-exit-request-form">
                <div className="text-sm font-medium">
                  {detail.outward_empty_exit_allowed
                    ? "Request approval to exit without loading"
                    : "Request manager approval to check out"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {detail.outward_empty_exit_allowed
                    ? "Use when the truck must leave empty from Sales Link, Tare, or Loading. After check-out it is removed from that phase."
                    : "Use when mill steps are incomplete but the vehicle must leave. Add notes and optional photos or documents."}
                </p>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason / notes *</Label>
                  <Textarea
                    className="mt-1.5 min-h-[80px]"
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Why should this vehicle be allowed out?"
                    data-testid="gate-exit-request-notes"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Support documents (optional)</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    className="mt-1.5"
                    onChange={(e) => setSupportFiles(e.target.files)}
                    data-testid="gate-exit-request-files"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Up to 5 files, 500 KB each (images or PDF).</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:w-auto"
                  disabled={busy}
                  onClick={submitApprovalRequest}
                  data-testid="gate-exit-request-submit"
                >
                  Submit for manager approval
                </Button>
              </div>
            )}

            {gp.status === "IN" && detail.can_review_exit_request && canApprove && (
              <div className="border border-blue-200 rounded-md p-4 space-y-3" data-testid="gate-exit-review-form">
                <div className="text-sm font-medium">Manager decision</div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Review notes</Label>
                  <Textarea
                    className="mt-1.5"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Required if rejecting"
                    data-testid="gate-exit-review-notes"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                    disabled={busy}
                    onClick={() => reviewRequest("APPROVE")}
                    data-testid="gate-exit-approve"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve exit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => reviewRequest("REJECT")}
                    data-testid="gate-exit-reject"
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          {gp?.id && (
            <div className="mr-auto">
              <AdminEntityActions
                entityType="gate_pass"
                entityId={gp.id}
                entityLabel={gp.code}
                onDeleted={() => {
                  onOpenChange(false);
                  onDeleted?.();
                  onUpdated?.();
                }}
              />
            </div>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {detail?.may_check_out && gp?.status === "IN" && (() => {
            const needsForce =
              canForce && !detail.can_check_out && !detail.has_approved_override && detail.can_force_check_out;
            const label = detail.can_check_out
              ? "Check out"
              : detail.has_approved_override
              ? "Check out (approved)"
              : "Force check out";
            return (
              <Button
                type="button"
                className={needsForce ? "" : "bg-bansal-sage hover:bg-bansal-sageDark text-white"}
                variant={needsForce ? "destructive" : "default"}
                disabled={busy}
                onClick={() => checkOut(needsForce)}
                data-testid={needsForce ? "gate-force-checkout-button" : "gate-checkout-button"}
              >
                <LogOut className="h-4 w-4 mr-1" /> {label}
              </Button>
            );
          })()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{children}</div>
    </div>
  );
}
